import { ClockChunky } from '@eventbrite/legacy-eds.iconography';
import { TextList } from '@eventbrite/legacy-eds.text-list';
import { setWindowLocation } from '@eventbrite/legacy-infra.http';
import { getNavigatorLastRecentSearchTerms } from '@eventbrite/consumer-search.personalization';
import { logEvent } from '@eventbrite/legacy-infra.statsig';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useRef } from 'react';
import { useIntersection } from 'react-use';
import { CompletionType } from '../../api/searchAutocomplete';
import { ItemContent } from '../../components/ItemContent';
import { STATSIG_CLICK_AUTOCOMPLETE, STATSIG_VIEW_AUTOCOMPLETE } from '../../constants';
import { useSearchInputContext } from '../../context';
import { useRunSearch, useSearchAutocompleteQuery } from '../../hooks';
import { AutocompleteOrganizer } from '../../types/search';
import { OrganizerResult } from './OrganizerResult';
import styles from './SearchSuggestions.module.scss';

interface QueryItemProps {
    isRecent?: boolean;
}

interface QueryData {
    content: JSX.Element | Element | null;
    value: string;
}

interface SearchQuerySuggestionsProps {
    completionTypes: CompletionType[];
    query?: string;
}

const dividerConfig = (showSectionDivider: boolean, index: number, length: number) => ({
    showDivider: showSectionDivider && index === length - 1,
    dividerVertSpacing: 3,
    getDivider: ({
        showDivider,
        dividerComponent,
    }: {
        showDivider: boolean;
        dividerComponent: JSX.Element;
    }) => (showDivider ? dividerComponent : null),
});

const formatQueryData = ({
    query,
    userQuery,
    config,
    showSectionDivider,
}: {
    query: string[];
    userQuery: string;
    config: QueryItemProps;
    showSectionDivider: boolean;
}) => {
    let queryData: QueryData[] = [];
    if (query) {
        queryData = query?.map((item: string, index: number) => {
            return {
                content: <ItemContent {...config} content={item} userQuery={userQuery} />,
                iconType: config.isRecent ? <ClockChunky /> : null,
                value: config.isRecent ? `recent--${item}` : item,
                ...dividerConfig(showSectionDivider, index, query.length),
            };
        });
    }
    return queryData;
};

const formatOrganizerData = ({ organizers }: { organizers: AutocompleteOrganizer[] }) => {
    return organizers.map((organizer) => {
        const organizerId = organizer.organizer_id.split('/')[1];

        return {
            content: (
                <OrganizerResult
                    organizerName={organizer.organizer_name}
                    eventCount={organizer.event_count}
                />
            ),
            iconType: null,
            value: `organizer--${organizerId}`,
        };
    });
};

const useQueryResponse = ({ completionTypes }: { completionTypes: CompletionType[] }) => {
    const { data, debouncedQuery } = useSearchAutocompleteQuery({
        completionTypes,
    });

    const formattedItems = React.useMemo(() => {
        let queryResults: QueryData[] = [];
        let organizerResults: QueryData[] = [];

        const hasQueryResults = (data?.query?.length ?? 0) > 0;
        const hasOrganizerResults = (data?.organizer?.length ?? 0) > 0;

        if (hasQueryResults) {
            queryResults = formatQueryData({
                query: data?.query || [''],
                userQuery: debouncedQuery,
                config: { isRecent: false },
                showSectionDivider: hasOrganizerResults,
            });
        }

        if (hasOrganizerResults) {
            organizerResults = formatOrganizerData({
                organizers: data?.organizer || [],
            });
        }

        return {
            queryResults,
            organizerResults,
        };
    }, [data?.query, data?.organizer, debouncedQuery]);

    return formattedItems;
};

const useCachedQueryResponse = ({
    showSectionDivider = false,
}: {
    showSectionDivider?: boolean;
}) => {
    const { query } = useSearchInputContext();
    const historySuggestions = getNavigatorLastRecentSearchTerms();
    const userQuery = query || '';

    const items = React.useMemo(() => {
        let items: QueryData[] = [];
        const filteredSuggestions = historySuggestions
            .filter((str: string) => str.includes(userQuery))
            .map((str: string) => str.trim())
            .slice(0, 5) || [''];

        if (!isEmpty(historySuggestions)) {
            items = formatQueryData({
                query: filteredSuggestions,
                userQuery: userQuery,
                config: { isRecent: true },
                showSectionDivider,
            });
        }
        return items;
    }, [userQuery, historySuggestions, showSectionDivider]);

    return items;
};

/**
 * Render appropriate Suggestion components for when
 * SearchQuery is focused or truthy.
 */
export const SearchQuerySuggestions: React.FunctionComponent<SearchQuerySuggestionsProps> = ({
    completionTypes,
}) => {
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const impressionTrackedRef = useRef<boolean>(false);

    const { queryResults, organizerResults } = useQueryResponse({
        completionTypes,
    });

    const recentItems = useCachedQueryResponse({
        showSectionDivider: queryResults.length > 0 || organizerResults.length > 0,
    });

    const handleSearch = useRunSearch();
    const { locale, usesLanguageSubdirectory, pageType = 'Unknown' } = useSearchInputContext();

    const limitResults = organizerResults.length ? 3 : 5;

    const mergedItems = useMemo(
        () => [
            ...recentItems.slice(0, limitResults),
            ...queryResults.slice(0, limitResults),
            ...organizerResults.slice(0, limitResults),
        ],
        [recentItems, queryResults, organizerResults],
    );

    const intersection = useIntersection(suggestionsRef, {
        threshold: 0.25,
        root: null,
    });

    useEffect(() => {
        if (!impressionTrackedRef.current && intersection?.isIntersecting) {
            impressionTrackedRef.current = true;
            logEvent(STATSIG_VIEW_AUTOCOMPLETE, null, {
                Screen: pageType,
                RecentSearch: String(recentItems.length > 0),
                Keyword: String(queryResults.length > 0),
                Organizer: String(organizerResults.length > 0),
            });
        }
    }, [
        intersection?.isIntersecting,
        mergedItems,
        recentItems.length,
        queryResults.length,
        organizerResults.length,
        pageType,
    ]);

    const handleItemClick = (val: string) => {
        if (val.includes('recent--')) {
            const [, q] = val.split('recent--');

            logEvent(STATSIG_CLICK_AUTOCOMPLETE, null, {
                Screen: pageType,
                RecentSearch: 'true',
            });

            return handleSearch({
                search: { q },
                analytics: [
                    { action: 'RunSearch', label: q },
                    { action: 'AutocompleteSuggestionClicked', label: q },
                ],
            });
        } else if (val.includes('organizer--')) {
            const [, organizerId] = val.split('organizer--');

            let url = `/o/${organizerId}`;

            if (usesLanguageSubdirectory && locale) {
                url = `/${locale}${url}`;
            }

            logEvent(STATSIG_CLICK_AUTOCOMPLETE, null, {
                Screen: pageType,
                Organizer: 'true',
            });

            return setWindowLocation(url);
        }

        logEvent(STATSIG_CLICK_AUTOCOMPLETE, null, {
            Screen: pageType,
            KeywordClick: 'true',
        });

        handleSearch({
            search: { q: val },
            analytics: [
                { action: 'RunSearch', label: val },
                { action: 'AutocompleteSuggestionClicked', label: val },
            ],
        });
    };

    return (
        <div
            className={styles.searchSuggestions}
            data-testid="search-takeover__query-suggestions"
            ref={suggestionsRef}
        >
            <TextList
                id={'search-autocomplete-suggestions'}
                items={mergedItems}
                onItemSelect={(val: string) => handleItemClick(val)}
            />
        </div>
    );
};
